import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { listiniAPI } from '../utils/api';
import { Listino } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import toast from 'react-hot-toast';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Copy,
  List,
  Loader2,
  Star,
  Calendar,
} from 'lucide-react';

export default function PriceLists() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('listini:create');
  const canEdit = hasPermission('listini:edit');
  const canDelete = hasPermission('listini:delete');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statoFilter, setStatoFilter] = useState<string>('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data: listiniData, isLoading } = useQuery({
    queryKey: ['listini', page, search, statoFilter],
    queryFn: async () => {
      const response = await listiniAPI.getListini({
        page,
        limit: 20,
        search: search || undefined,
        is_attivo: statoFilter === '' ? undefined : statoFilter === 'attivo',
      });
      return response.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => listiniAPI.deleteListino(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product'] });
      queryClient.invalidateQueries({ queryKey: ['listini'] });
      toast.success('Listino eliminato con successo');
      setDeleteConfirm(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    },
  });

  const cloneMutation = useMutation({
    mutationFn: (id: string) => listiniAPI.cloneListino(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product'] });
      queryClient.invalidateQueries({ queryKey: ['listini'] });
      toast.success('Listino clonato con successo');
      navigate(`/price-lists/${response.data.id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nella clonazione');
    },
  });

  const getStatusBadge = (listino: Listino) => {
    if (!listino.is_attivo) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Inattivo
        </span>
      );
    }

    const today = new Date().toISOString().split('T')[0];
    if (listino.valido_a && listino.valido_a < today) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Scaduto
        </span>
      );
    }
    if (listino.valido_da && listino.valido_da > today) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Programmato
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Attivo
      </span>
    );
  };

  const listini = listiniData?.data || [];
  const pagination = listiniData?.pagination;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Listini Prezzi</h1>
          <p className="text-gray-600">Gestisci i listini prezzi e le politiche di pricing</p>
        </div>
        {canCreate && (
          <button
            onClick={() => navigate('/price-lists/new')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-5 w-5" />
            Nuovo Listino
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca per nome, codice..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={statoFilter}
            onChange={(e) => {
              setStatoFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tutti gli stati</option>
            <option value="attivo">Attivi</option>
            <option value="inattivo">Inattivi</option>
          </select>
        </div>
      </div>

      {/* Listini Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : listini.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <List className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Nessun listino trovato</p>
            {canCreate && (
              <button
                onClick={() => navigate('/price-lists/new')}
                className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
              >
                Crea il primo listino
              </button>
            )}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Listino
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Codice
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Validità
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {listini.map((listino) => (
                <tr
                  key={listino.id}
                  className={canEdit ? 'hover:bg-gray-50 cursor-pointer' : ''}
                  onClick={canEdit ? () => navigate(`/price-lists/${listino.id}`) : undefined}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {listino.nome}
                      </span>
                      {listino.is_base && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <Star className="h-3 w-3" />
                          Base
                        </span>
                      )}
                    </div>
                    {listino.descrizione && (
                      <p className="text-sm text-gray-500 truncate max-w-xs">
                        {listino.descrizione}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {listino.codice || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {getStatusBadge(listino)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      {listino.valido_da
                        ? new Date(listino.valido_da).toLocaleDateString('it-IT')
                        : '∞'}
                      {' → '}
                      {listino.valido_a
                        ? new Date(listino.valido_a).toLocaleDateString('it-IT')
                        : '∞'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                      {canEdit && (
                        <button
                          onClick={() => navigate(`/price-lists/${listino.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Modifica"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                      )}
                      <button
                        onClick={() => cloneMutation.mutate(listino.id)}
                        className="text-green-600 hover:text-green-900"
                        title="Clona"
                        disabled={cloneMutation.isPending}
                      >
                        <Copy className="h-5 w-5" />
                      </button>
                      {canDelete && (
                        <button
                          onClick={() => setDeleteConfirm(listino.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Elimina"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Pagina {pagination.page} di {pagination.pages} ({pagination.total} listini)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Precedente
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Successivo
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Conferma Eliminazione</h3>
            <p className="text-gray-600 mb-6">
              Sei sicuro di voler eliminare questo listino? L'operazione non può essere annullata.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Elimina'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
